import React, { useState, useEffect } from 'react';
import { Card, Row, Col, Statistic, Table, Tag, Typography, Alert, Spin } from 'antd';
import {
  MobileOutlined,
  CheckCircleOutlined,
  ExclamationCircleOutlined,
  ClockCircleOutlined
} from '@ant-design/icons';
import { deviceAPI } from '../utils/api';
import { Device, DeviceStats } from '../types';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';

dayjs.extend(utc);


const { Title } = Typography;

const Dashboard: React.FC = () => {
  const [stats, setStats] = useState<DeviceStats | null>(null);
  const [devices, setDevices] = useState<Device[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      setLoading(true);
      setError(null);

      const [statsData, devicesData] = await Promise.all([
        deviceAPI.getStats(),
        deviceAPI.getDevices()
      ]);

      setStats(statsData);
      setDevices(devicesData.slice(0, 10)); // 只显示最近的10个设备
    } catch (err: any) {
      setError(err.message || '获取数据失败');
      console.error('获取数据失败:', err);
    } finally {
      setLoading(false);
    }
  };

  const getDeviceStatus = (device: Device) => {
    if (device.disabled) {
      return <Tag color="red">已禁用</Tag>;
    }

    if (device.lastCommunicationTime) {
      const lastCommLocal = dayjs.utc(device.lastCommunicationTime).local();
      const hoursSinceComm = dayjs().diff(lastCommLocal, 'hour');

      if (hoursSinceComm < 24) {
        return <Tag color="green">在线</Tag>;
      } else if (hoursSinceComm < 72) {
        return <Tag color="orange">最近活跃</Tag>;
      }
    }

    return <Tag color="red">离线</Tag>;
  };

  const columns = [
    {
      title: '设备代码',
      dataIndex: 'deviceCode',
      key: 'deviceCode',
    },
    {
      title: '用户身份',
      dataIndex: 'userIdentity',
      key: 'userIdentity',
      render: (text: string) => text || '-',
    },
    {
      title: '描述',
      dataIndex: 'description',
      key: 'description',
      render: (text: string) => text || '-',
    },
    {
      title: '状态',
      key: 'status',
      render: (_: any, record: Device) => getDeviceStatus(record),
    },
    {
      title: '最后通信时间',
      dataIndex: 'lastCommunicationTime',
      key: 'lastCommunicationTime',
      render: (text: string) => text ? dayjs.utc(text).local().format('YYYY-MM-DD HH:mm:ss') : '-',
    },
  ];

  if (loading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '400px' }}>
        <Spin size="large" tip="加载中..." />
      </div>
    );
  }

  if (error) {
    return (
      <Alert
        message="加载失败"
        description={error}
        type="error"
        showIcon
        style={{ margin: '20px 0' }}
      />
    );
  }

  return (
    <div>
      <div className="page-header">
        <Title level={2} style={{ margin: 0 }}>
          📊 仪表盘
        </Title>
        <p style={{ margin: '8px 0 0 0', color: '#666' }}>
          设备管理系统概览
        </p>
      </div>

      {/* 统计卡片 */}
      <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
        <Col xs={24} sm={12} md={6}>
          <Card>
            <Statistic
              title="设备总数"
              value={stats?.total || 0}
              prefix={<MobileOutlined />}
              valueStyle={{ color: '#1890ff' }}
              className="stats-card"
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} md={6}>
          <Card>
            <Statistic
              title="活跃设备"
              value={stats?.active || 0}
              prefix={<CheckCircleOutlined />}
              valueStyle={{ color: '#52c41a' }}
              className="stats-card"
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} md={6}>
          <Card>
            <Statistic
              title="最近活跃"
              value={stats?.recentlyActive || 0}
              prefix={<ClockCircleOutlined />}
              valueStyle={{ color: '#faad14' }}
              className="stats-card"
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} md={6}>
          <Card>
            <Statistic
              title="已禁用"
              value={stats?.disabled || 0}
              prefix={<ExclamationCircleOutlined />}
              valueStyle={{ color: '#ff4d4f' }}
              className="stats-card"
            />
          </Card>
        </Col>
      </Row>

      {/* 最近设备列表 */}
      <Card
        title="最近设备"
        className="content-card"
        extra={<span style={{ color: '#666' }}>显示最近注册的设备</span>}
      >
        <Table
          columns={columns}
          dataSource={devices}
          rowKey="id"
          pagination={false}
          className="device-table"
        />
      </Card>
    </div>
  );
};

export default Dashboard;
